import React, { useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";

/** Item definition for the dropdown menu list. */
export interface DropdownMenuItem {
  value: string;
  label: string;
  iconName: string;
  href: string;
  tone?: "default" | "danger";
}

/** Props for the dropdown menu component. */
export interface DropdownMenuProps {
  companyName: string;
  avatarSrc?: string;
  items: DropdownMenuItem[];
  isOpen?: boolean;
  defaultOpen?: boolean;
  onToggle?: (open: boolean) => void;
  onItemClick?: (
    item: DropdownMenuItem,
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => void;
}

const WIDTH = 264;
const HEADER_HEIGHT = 64;
const HEADER_RADIUS = 24;

/** Dropdown menu with optional avatar header and selectable items. */
export const DropdownMenu = ({
  companyName,
  avatarSrc,
  items,
  isOpen,
  defaultOpen = false,
  onToggle,
  onItemClick,
}: DropdownMenuProps): React.ReactElement => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const resolvedOpen = isOpen ?? internalOpen;
  const initials = useMemo((): string => {
    const parts = companyName.trim().split(/\s+/);
    const letters = parts
      .map((part) => part[0])
      .slice(0, 2)
      .join("");
    return letters.toUpperCase();
  }, [companyName]);

  const handleToggle = (): void => {
    const next = !resolvedOpen;
    if (isOpen === undefined) {
      setInternalOpen(next);
    }
    onToggle?.(next);
  };

  const handleItemClick = (
    item: DropdownMenuItem,
    event: React.MouseEvent<HTMLAnchorElement>,
  ): void => {
    onItemClick?.(item, event);
    if (isOpen === undefined) {
      setInternalOpen(false);
    }
    onToggle?.(false);
  };

  return (
    <Wrapper>
      <Header type="button" onClick={handleToggle} $isOpen={resolvedOpen}>
        {avatarSrc ? (
          <Avatar src={avatarSrc} alt={`${companyName} logo`} />
        ) : (
          <AvatarFallback>{initials}</AvatarFallback>
        )}
        <CompanyName>{companyName}</CompanyName>
        <Chevron>
          <CustomIcon
            name={resolvedOpen ? "v2/up-chevron" : "v2/down-chevron"}
            size="24px"
            color={ColorV2.Blue5}
          />
        </Chevron>
      </Header>
      {resolvedOpen && (
        <Menu>
          {items.map((item) => (
            <MenuItem
              key={item.value}
              href={item.href}
              $tone={item.tone ?? "default"}
              onClick={(event) => handleItemClick(item, event)}
            >
              <CustomIcon
                name={item.iconName}
                size="24px"
                color={
                  item.tone === "danger" ? ColorV2.NegativeRed : ColorV2.Blue1
                }
              />
              <span>{item.label}</span>
            </MenuItem>
          ))}
        </Menu>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: ${WIDTH}px;
`;

const Header = styled.button<{ $isOpen: boolean }>`
  ${TypographyV2.b1}
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 16px;
  width: ${WIDTH}px;
  height: ${HEADER_HEIGHT}px;
  padding: 8px 16px;
  border: none;
  border-radius: ${(props) =>
    props.$isOpen
      ? `${HEADER_RADIUS}px ${HEADER_RADIUS}px 0 0`
      : `${HEADER_RADIUS}px`};
  background-color: ${ColorV2.White};
  cursor: pointer;
  box-shadow: ${(props) =>
    props.$isOpen ? "none" : "0 4px 20px rgba(0, 0, 0, 0.15)"};
`;

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  ${TypographyV2.b1}
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${ColorV2.Blue6};
  color: ${ColorV2.Blue1};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CompanyName = styled.span`
  ${TypographyV2.b1}
  color: ${ColorV2.Blue1};
  flex: 1;
  text-align: left;
`;

const Chevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% - 8px);
  left: 0;
  z-index: 1;
  width: ${WIDTH}px;
  padding: 16px 8px 12px;
  border-radius: 0 0 24px 24px;
  background-color: ${ColorV2.White};
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const MenuItem = styled.a<{ $tone: "default" | "danger" }>`
  ${TypographyV2.b1}
  display: inline-flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 8px 8px;
  min-height: 32px;
  text-decoration: none;
  color: ${(props) =>
    props.$tone === "danger" ? ColorV2.NegativeRed : ColorV2.Blue1};
  border-radius: 12px;

  &:hover {
    background-color: ${ColorV2.Blue7};
  }

  &:hover svg,
  &:hover svg * {
    color: ${(props) =>
      props.$tone === "danger" ? ColorV2.NegativeRed : ColorV2.Blue1};
  }

  ${(props) =>
    props.$tone === "danger" &&
    css`
      svg,
      svg * {
        color: ${ColorV2.NegativeRed};
      }
    `}
`;
